import React, { useState, useEffect } from 'react';
import { Trash } from 'lucide-react';
import { ConfirmationModal } from '../ui/ConfirmationModal';

const KeyManager = () => {
    const [providers, setProviders] = useState([]);
    const [newProvider, setNewProvider] = useState({ name: '', key: '' });
    const [deleteModalState, setDeleteModalState] = useState({ isOpen: false, providerName: null });

    const fetchKeys = async () => {
        try {
            const res = await fetch('/api/admin/ai/keys');
            if (res.ok) {
                const data = await res.json();
                setProviders(data);
            }
        } catch (err) {
            console.error("Failed to fetch keys", err);
        }
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await fetch('/api/admin/ai/keys');
                if (res.ok && mounted) {
                    const data = await res.json();
                    setProviders(data);
                }
            } catch (err) {
                console.error("Failed to fetch keys", err);
            }
        };
        load();
        return () => { mounted = false; };
    }, []);

    const handleAdd = async () => {
        if (!newProvider.name || !newProvider.key) return;
        await fetch('/api/admin/ai/keys', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ provider_name: newProvider.name, key: newProvider.key })
        });
        setNewProvider({ name: '', key: '' });
        fetchKeys();
    };

    const handleDeleteClick = (name) => {
        setDeleteModalState({ isOpen: true, providerName: name });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModalState.providerName) return;
        await fetch(`/api/admin/ai/keys/${deleteModalState.providerName}`, { method: 'DELETE' });
        fetchKeys();
        setDeleteModalState({ isOpen: false, providerName: null });
    };

    return (
        <div className="h-full flex flex-col gap-6 text-stone-300 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white">API Key Management</h3>

            <div className="bg-stone-900 border border-stone-800 rounded-lg p-6 flex flex-col gap-4">
                <h4 className="font-bold text-sm text-stone-400">Add New Key</h4>
                <div className="grid grid-cols-1 gap-4">
                    <input
                        className="bg-stone-800 border border-stone-700 p-2 rounded"
                        placeholder="Provider Name (e.g. openai, anthropic)"
                        value={newProvider.name}
                        onChange={e => setNewProvider({ ...newProvider, name: e.target.value })}
                    />
                    <div className="flex gap-2">
                        <input
                            className="flex-1 bg-stone-800 border border-stone-700 p-2 rounded font-mono"
                            type="password"
                            placeholder="API Key (stored encrypted)"
                            value={newProvider.key}
                            onChange={e => setNewProvider({ ...newProvider, key: e.target.value })}
                        />
                        <button onClick={handleAdd} className="bg-green-600 text-white px-4 rounded font-bold hover:bg-green-700">Add</button>
                    </div>
                </div>
            </div>

            <div className="flex-1">
                <h4 className="font-bold text-sm text-stone-400 mb-2">Active Providers</h4>
                <div className="flex flex-col gap-2">
                    {providers.map(p => (
                        <div key={p.id} className="bg-stone-800/50 border border-stone-700 p-4 rounded flex justify-between items-center">
                            <span className="font-bold text-white uppercase tracking-wider">{p.provider_name}</span>
                            <div className="flex items-center gap-4">
                                <span className="text-xs text-green-400 bg-green-900/20 px-2 py-1 rounded">Active & Encrypted</span>
                                <button onClick={() => handleDeleteClick(p.provider_name)} className="text-stone-500 hover:text-red-400">
                                    <Trash size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                    {providers.length === 0 && <div className="text-stone-500 italic">No keys configured.</div>}
                </div>
            </div>

            <ConfirmationModal
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, providerName: null })}
                onConfirm={handleConfirmDelete}
                title="Delete Key"
                message={`Are you sure you want to delete the API key for ${deleteModalState.providerName}?`}
            />
        </div>
    );
};

export default KeyManager;
